import { useEffect, useState } from 'react'
import { toast } from '@sales-center/components'
import type { Activity } from '@user/features/Activity/typings'
import { fetchActivity } from '@user/features/Activity/components/EditActivityPopover/EditActivityForm/effects/fetchActivity.effect'

// SH-1432: load the full Activity for the edit dialog. The ActivitiesContext seeds
// `current`/`activities` at mount, so the record must be present before the form
// renders — this hook gates that. `status === 'failed'` lets the host close on a
// load error (fetchActivity already toasts).

export type EditActivityStatus = 'idle' | 'loading' | 'loaded' | 'failed'

export type EditActivityState = {
  activity: Activity | null
  status: EditActivityStatus
}

const IDLE: EditActivityState = { activity: null, status: 'idle' }

export function useEditActivity(
  editActivityId: number | undefined,
): EditActivityState {
  // One atomic state object rather than parallel useState flags — activity and
  // status always move together, so a single write can't leave them inconsistent.
  const [state, setState] = useState<EditActivityState>(IDLE)

  useEffect(() => {
    if (editActivityId == null) {
      setState(IDLE)
      return
    }

    let mounted = true
    setState({ activity: null, status: 'loading' })

    fetchActivity(editActivityId)
      .then((result) => {
        if (!mounted) return
        setState(
          result
            ? { activity: result, status: 'loaded' }
            : { activity: null, status: 'failed' },
        )
      })
      .catch(() => {
        // fetchActivity resolves undefined (after toasting) for any failed
        // response, so it only rejects when a 2xx body is not JSON — a rarity, but
        // without this catch the promise would stay pending, status would sit on
        // 'loading' forever and the host would hang on the spinner — the auto-close
        // effect only fires on 'failed'. Treat a rejection as a load failure. It
        // rejects before fetchActivity could toast, so toast here or the dialog
        // would close with no explanation.
        if (!mounted) return
        toast.error('Failed to load Activity')
        setState({ activity: null, status: 'failed' })
      })

    return () => {
      mounted = false
    }
  }, [editActivityId])

  return state
}
